import { useRef, ReactNode, KeyboardEvent, useEffect } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { Identifier, XYCoord } from 'dnd-core';

import { DIRECTION_LEFT, DIRECTION_RIGHT } from 'webclient.constants';
import { DragItem, SORTABLE_CARD, SortResult } from './sortable.constants';
import * as Styled from './sortable-card.style';

type Props = {
  sourceIndex: number;
  index: number;
  children: ReactNode;
  move: (sourceIndex: number, destinationIndex: number) => void;
  onDrop: (result: SortResult) => void;
  onDidNotDrop: () => void;
  handleMoveWithKeyDown: (
    e: KeyboardEvent<HTMLDivElement>,
    sourceIndex: number
  ) => void;
};

export const SortableCard = ({
  sourceIndex,
  children,
  index,
  move,
  onDrop,
  onDidNotDrop,
  handleMoveWithKeyDown,
}: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const dragLayerRef = useRef<HTMLDivElement>(null);

  const [{ handlerId }, drop] = useDrop<
    DragItem,
    void,
    { handlerId: Identifier | null }
  >({
    accept: SORTABLE_CARD,
    collect: monitor => ({
      handlerId: monitor.getHandlerId(),
    }),
    hover: (item: DragItem, monitor) => {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      move(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
    drop(data: DragItem) {
      onDrop({
        sourceIndex: data.sourceIndex,
        destinationIndex: data.index,
        direction:
          data.sourceIndex < data.index ? DIRECTION_RIGHT : DIRECTION_LEFT,
      });
    },
  });

  const [{ isDragging }, drag, preview] = useDrag({
    type: SORTABLE_CARD,
    item: () => ({ sourceIndex, index, el: ref.current }),
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
    end: (_: any, monitor) => {
      const didDrop = monitor.didDrop();
      if (!didDrop) {
        onDidNotDrop();
      }
    },
  });

  drop(ref);
  drag(dragLayerRef);

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
    // eslint-disable-next-line
  }, []);

  return (
    <Styled.SortableCardWrapper
      ref={ref}
      data-handler-id={handlerId}
      $isDragging={isDragging}
      data-selector={`sortable-card-${index}`}
      onKeyDown={e => handleMoveWithKeyDown(e, sourceIndex)}
    >
      <Styled.SortableCardDragLayer ref={dragLayerRef} />
      <Styled.SortableCardContent>{children}</Styled.SortableCardContent>
    </Styled.SortableCardWrapper>
  );
};
